import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  Box, Typography, Button, TextField, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Chip, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, InputAdornment,
  CircularProgress, TablePagination, Snackbar, Skeleton
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Alert } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { fetchPartners, addPartner } from "../redux/partners.action";
import { clearError } from "../redux/partners.slice";

const useStyles = makeStyles(() => ({
  pageCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: 4,
    minHeight: "calc(100vh - 160px)",
    padding: "20px 24px",
  },
  pageTitle: {
    fontWeight: 700,
    fontSize: 13,
    color: "#3A4D54",
    marginBottom: 4,
  },
  accentBar: {
    width: 30,
    height: 3,
    backgroundColor: "#3A4D54",
    borderRadius: 2,
    marginBottom: 20,
  },
  filterRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 20,
    paddingBottom: 16,
    borderBottom: "1px solid #F2F4F7",
  },
  search: {
    width: 400,
  },
  addBtn: {
    border: "1px solid #3A4D54",
    color: "#3A4D54",
    fontWeight: 700,
    fontSize: 14,
    padding: "6px 16px",
    "&:hover": {
      backgroundColor: "#F9FAFB",
    },
  },
  tableContainer: {
    border: "none",
    borderRadius: 0,
    boxShadow: "none",
  },
  chipActive: {
    backgroundColor: "#ECFDF3",
    color: "#067647",
    fontWeight: 500,
  },
  chipPending: {
    backgroundColor: "#FFF4E5",
    color: "#B54708",
    fontWeight: 500,
  },
  chipInactive: {
    backgroundColor: "#F2F4F7",
    color: "#344054",
    fontWeight: 500,
  },
}));

const statusLabels = { 1: "Pending", 2: "Active", 3: "Deactivated" };

export default function PartnersListPage() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const { data, totalRecords, isLoading, error } = useSelector((state) => state.partners);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ name: "", contact_email: "" });
  const [formErrors, setFormErrors] = useState({});
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    dispatch(fetchPartners({ search, page: page + 1, length: rowsPerPage }));
  }, [dispatch, search, page, rowsPerPage]);

  const validateForm = () => {
    const errors = {};
    if (!form.name.trim()) errors.name = "Name is required";
    if (!form.contact_email.trim()) errors.contact_email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contact_email)) errors.contact_email = "Invalid email format";
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreate = async () => {
    if (!validateForm()) return;
    setIsSubmitting(true);
    const result = await dispatch(addPartner(form));
    setIsSubmitting(false);
    if (result.success) {
      setDialogOpen(false);
      setForm({ name: "", contact_email: "" });
      setFormErrors({});
      setSnack({ open: true, message: "Partner created successfully!", severity: "success" });
      dispatch(fetchPartners({ search, page: page + 1, length: rowsPerPage }));
    } else {
      setSnack({ open: true, message: result.message || "Failed to create partner", severity: "error" });
    }
  };

  const getChipClass = (status) => {
    if (status === 2) return classes.chipActive;
    if (status === 1) return classes.chipPending;
    return classes.chipInactive;
  };

  return (
    <Box className={classes.pageCard}>
      <Typography className={classes.pageTitle}>Partners</Typography>
      <Box className={classes.accentBar} />
      <Box className={classes.filterRow}>
        <TextField
          className={classes.search}
          variant="outlined"
          size="small"
          placeholder="Search name or email"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon style={{ color: "#98A2B3" }} /></InputAdornment> }}
        />
        <Button
          variant="outlined"
          className={classes.addBtn}
          startIcon={<AddIcon />}
          onClick={() => setDialogOpen(true)}
        >
          Add Partner
        </Button>
      </Box>
      {error && (
        <Alert severity="error" style={{ marginBottom: 16 }} onClose={() => dispatch(clearError())}>{error}</Alert>
      )}
      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Companies</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 8 }).map((_, i) => (
                <TableRow key={`skeleton-${i}`}>
                  <TableCell><Skeleton variant="text" width={140} /></TableCell>
                  <TableCell><Skeleton variant="text" width={180} /></TableCell>
                  <TableCell><Skeleton variant="rounded" width={70} height={20} sx={{ borderRadius: 12 }} /></TableCell>
                  <TableCell><Skeleton variant="text" width={30} /></TableCell>
                  <TableCell align="right"><Skeleton variant="circular" width={24} height={24} sx={{ ml: "auto" }} /></TableCell>
                </TableRow>
              ))
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" style={{ color: "#98A2B3" }}>No partners found</TableCell>
              </TableRow>
            ) : (
              data.map((partner) => (
                <TableRow key={partner.id} hover>
                  <TableCell>{partner.name}</TableCell>
                  <TableCell>{partner.contact_email || "-"}</TableCell>
                  <TableCell>
                    <Chip
                      label={statusLabels[partner.status]}
                      size="small"
                      className={getChipClass(partner.status)}
                    />
                  </TableCell>
                  <TableCell>{partner.companies?.length || 0}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => history.push(`/admin/partners/${partner.uuid}`)} style={{ color: "#3A4D54" }}>
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        {!isLoading && (
          <TablePagination
            component="div"
            count={totalRecords}
            page={page}
            onPageChange={(e, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          />
        )}
      </TableContainer>
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography style={{ fontWeight: 700, fontSize: 14, color: "#3A4D54" }}>Add Partner</Typography>
            <IconButton size="small" onClick={() => { setDialogOpen(false); setFormErrors({}); }}><CloseIcon style={{ fontSize: 18 }} /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box bgcolor="#fff" borderRadius="4px" px="10px" py="20px">
            <Box display="flex" flexDirection="column" style={{ gap: 20 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography style={{ fontSize: 14, color: "#344054" }}>Name <span style={{ color: "#F97066" }}>*</span></Typography>
                <TextField variant="outlined" size="small" placeholder="Please fill in partner name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} error={!!formErrors.name} helperText={formErrors.name} style={{ width: 360 }} inputProps={{ style: { fontSize: 14, height: 18, padding: "10px" } }} />
              </Box>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography style={{ fontSize: 14, color: "#344054" }}>Email <span style={{ color: "#F97066" }}>*</span></Typography>
                <TextField variant="outlined" size="small" placeholder="Please fill in partner email" type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} error={!!formErrors.contact_email} helperText={formErrors.contact_email} style={{ width: 360 }} inputProps={{ style: { fontSize: 14, height: 18, padding: "10px" } }} />
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCreate} variant="contained" disabled={isSubmitting} style={{ backgroundColor: "#3A4D54", color: "#fff", fontWeight: 700, fontSize: 13, minWidth: 156, height: 32, borderRadius: 4, boxShadow: "0px 2px 2px rgba(34,45,48,0.15)" }}>
            {isSubmitting ? <CircularProgress size={20} color="inherit" /> : "CONFIRM"}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack({ ...snack, open: false })}>
        <Alert severity={snack.severity} onClose={() => setSnack({ ...snack, open: false })}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
}
